// Esc handling for stacked modals. DI's <Modal> closes on Esc with its own window listener, so
// with the edit modal (or the help popup) open on top of the manager modal one keypress would
// close every layer. Each open layer registers here; ONE capture-phase listener hands Esc to the
// topmost layer only and swallows the event so none of the Modals' own listeners see it.

import { useEffect, useRef } from 'react';

const stack = [];       // [{ id, onEsc: () => void }], topmost last
let installed = false;

function onKeyDown(e) {
  if (e.key !== 'Escape' || stack.length === 0) return;
  e.stopImmediatePropagation();
  e.preventDefault();
  const top = stack[stack.length - 1];
  top.onEsc();
}

function sync() {
  if (stack.length > 0 && !installed) {
    window.addEventListener('keydown', onKeyDown, true);
    installed = true;
  } else if (stack.length === 0 && installed) {
    window.removeEventListener('keydown', onKeyDown, true);
    installed = false;
  }
}

// While `open`, this component is a layer; Esc calls the latest `onEsc` if it is the topmost.
export function useEscLayer(open, onEsc) {
  const ref = useRef(onEsc);
  ref.current = onEsc;
  useEffect(() => {
    if (!open) return undefined;
    const layer = { onEsc: () => ref.current?.() };
    stack.push(layer);
    sync();
    return () => {
      const i = stack.indexOf(layer);
      if (i >= 0) stack.splice(i, 1);
      sync();
    };
  }, [open]);
}
